import { useCallback, useEffect, useRef, useState } from "react"
import cc from "classcat"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import H2 from "components/atoms/H2"
import ScrollAnimate from "components/atoms/common/ScrollAnimate"
import QuoteIcon from "images/quote.svg"
import SpotifyIcon from "images/icons-social/spotify-color.svg"
import YouTubeIcon from "images/icons-social/youtube-color.svg"
import useTranslations from "hooks/useTranslations"
import { VodcastCoverImage } from "./images"

/**
 * Customer quotes and the vodcast, as a row of cards that scrolls sideways
 * (DAT-779). Three cards show at once on desktop, two on tablets, and one on
 * phones with the next card's edge peeking in so it is clear there is more.
 *
 * This replaced a three-card grid. The quotes are still ordinary translation
 * arrays, so adding one is a new entry in each array and nothing here.
 *
 * Simon Gallacher's quote is first because it is the only UK school, and the
 * vodcast card comes straight after it. The other quotes
 * are Finnish, so the page must not imply they are UK schools. Each of those
 * attributions carries its city, which does that work honestly. Keep it.
 *
 * The row is the browser's own horizontal scroll with scroll-snap, so swiping,
 * trackpads and keyboard focus all work without any code of ours; the arrows
 * only call scrollBy. It does not auto-advance: the longest quote is ~700
 * characters, and a card that moves on before the reader finishes it is worse
 * than no carousel. Cards size to their content rather than stretching to a
 * shared height, as the grid did, so the short quotes do not sit two-thirds
 * empty.
 */

type Slide =
  | { kind: "quote"; key: string; quote: string; name: string; role: string }
  | { kind: "vodcast"; key: string }

// "Name, Role, City" -> name on its own line, the rest beneath it.
const splitAuthor = (author: string) => {
  const [name, ...rest] = (author || "").split(",")
  return { name: name.trim(), role: rest.join(",").trim() }
}

function QuoteSlide({
  quote,
  name,
  role,
}: {
  quote: string
  name: string
  role: string
}) {
  return (
    <figure className="m-0 flex flex-col">
      <QuoteIcon
        aria-hidden="true"
        className="mb-5 h-9 w-9 shrink-0 text-kkm-lavender opacity-80"
      />

      <blockquote className="m-0 space-y-4">
        {/* Blank lines in the translation become separate paragraphs. */}
        {quote.split(/\n\s*\n/).map((paragraph) => (
          <p
            key={paragraph.slice(0, 40)}
            className="m-0 text-base leading-relaxed text-zinc-200"
          >
            {paragraph}
          </p>
        ))}
      </blockquote>

      <figcaption className="pt-6">
        <div className="border-t border-white/10 pt-5">
          <cite className="not-italic">
            <span className="block font-display text-base font-bold text-zinc-100">
              {name}
            </span>
            {role ? (
              <span className="mt-1 block text-sm leading-snug text-zinc-400">
                {role}
              </span>
            ) : null}
          </cite>
        </div>
      </figcaption>
    </figure>
  )
}

function VodcastSlide({
  title,
  text,
  spotifyUrl,
  youtubeUrl,
}: {
  title: string
  text: string
  spotifyUrl: string
  youtubeUrl: string
}) {
  const links = [
    {
      url: spotifyUrl,
      platform: "Spotify",
      label: "Listen on Spotify",
      Icon: SpotifyIcon,
    },
    {
      url: youtubeUrl,
      platform: "YouTube",
      label: "Watch on YouTube",
      Icon: YouTubeIcon,
    },
  ].filter((link) => link.url)

  /**
   * Clicks are counted in Plausible as "Vodcast: Click", split by platform.
   * Spotify's own analytics do not report where on the web a listener came
   * from, and they ignore UTM parameters, so this event is the only record of
   * how much traffic this card sends. Use clean links: Spotify's "Copy link"
   * adds `si` (a share ID tied to whoever copied it) and utm_source=copy-link,
   * and neither tells us anything.
   */
  const trackClick = (platform: string) => {
    window.plausible?.("Vodcast: Click", {
      props: { platform, placement: "uk-schools-testimonials" },
    })
  }

  return (
    <div className="flex flex-col">
      {/* The cover art already spells out "SomeBuddy Vodcast: Talking Online
          Safety", so the image stands in for the badge and visible heading.
          The heading stays for screen readers, which is why the image itself
          is decorative (alt=""): announcing both would repeat the title. */}
      <VodcastCoverImage className="mb-6 aspect-square w-full overflow-hidden rounded-2xl" />
      <h3 className="sr-only">{title}</h3>
      <p className="m-0 text-base leading-relaxed text-zinc-200">{text}</p>

      {links.length ? (
        <div className="flex flex-wrap gap-3 pt-6">
          {links.map(({ url, platform, label, Icon }) => (
            <a
              key={label}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick(platform)}
              className={cc([
                "inline-flex items-center gap-3 rounded-full bg-zinc-800 py-2.5 pl-2.5 pr-4 font-display text-base text-zinc-100 no-underline",
                "shadow-[inset_0_1px_0_rgba(255,255,255,0.07)]",
                "transition-[background-color,transform] duration-150 ease-out hover:bg-zinc-700 active:scale-[0.97]",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-kkm-lavender focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900",
              ])}
            >
              <Icon aria-hidden="true" className="h-6 w-6 shrink-0" />
              {label}
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default function UkTestimonials() {
  const {
    ukSchoolsTestimonialsTitle,
    ukSchoolsTestimonialQuotes,
    ukSchoolsTestimonialAuthors,
    ukSchoolsVodcastTitle,
    ukSchoolsVodcastText,
    ukSchoolsVodcastSpotifyUrl,
    ukSchoolsVodcastYoutubeUrl,
  } = useTranslations()

  const quotes: string[] = ukSchoolsTestimonialQuotes || []
  const authors: string[] = ukSchoolsTestimonialAuthors || []

  const slides: Slide[] = quotes.map((quote, index) => ({
    kind: "quote",
    key: quote.slice(0, 40),
    quote,
    ...splitAuthor(authors[index]),
  }))
  // The vodcast goes second, straight after Simon's quote, so both UK-facing
  // cards are on screen together before the Finnish quotes.
  if (ukSchoolsVodcastTitle) {
    slides.splice(1, 0, { kind: "vodcast", key: "vodcast" })
  }

  const trackRef = useRef<HTMLUListElement>(null)
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)

  // Arrows are disabled at either end rather than wrapping: with several
  // cards on screen, jumping from the last back to the first reads as a glitch.
  const updateArrows = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    // A pixel of slack: scroll positions can land on fractions.
    setCanPrev(track.scrollLeft > 1)
    setCanNext(track.scrollLeft + track.clientWidth < track.scrollWidth - 1)
  }, [])

  useEffect(() => {
    updateArrows()
    window.addEventListener("resize", updateArrows)
    return () => window.removeEventListener("resize", updateArrows)
  }, [updateArrows])

  if (!slides.length) return null

  // One card plus the gap after it, so each click lands on the next snap point.
  const scrollByCard = (direction: 1 | -1) => {
    // The arrows use aria-disabled, so a press at the end still lands here.
    if (direction === 1 ? !canNext : !canPrev) return
    const track = trackRef.current
    const card = track?.firstElementChild as HTMLElement | null
    if (!track || !card) return
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches
    track.scrollBy({
      left: direction * (card.offsetWidth + gap),
      behavior: reduceMotion ? "auto" : "smooth",
    })
  }

  const title = ukSchoolsTestimonialsTitle?.replace(/\n/g, " ")

  // Same arrows as the parent info page's blog carousel (parentInfo/Blog.tsx),
  // which sits on the same dark band, so the two carousels look alike.
  // Unlike there, an arrow at the end of the row is aria-disabled, not
  // disabled: a disabled button drops keyboard focus to <body>, so a keyboard
  // or screen-reader user pressing Next onto the last card would lose their
  // place. aria-disabled keeps focus on the arrow and still announces it as
  // unavailable.
  const arrowClass =
    "inline-flex h-10 w-10 items-center justify-center rounded-full bg-kkm-green text-zinc-900 transition-[background-color,opacity,transform] duration-150 ease-out hover:bg-white active:scale-[0.96] aria-disabled:cursor-default aria-disabled:opacity-30 aria-disabled:hover:bg-kkm-green aria-disabled:active:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-800"

  return (
    <div className="bg-zinc-800 py-16 lg:py-24">
      <section
        className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
        aria-roledescription="carousel"
        aria-label={title}
      >
        {/* Arrows are 40px, no taller than one line of the heading, and
            centred on it, so they never add height above or below the title.
            They show on phones too: on a narrow screen they are the clearest
            sign that the row scrolls. `!mb-0` because H2 brings its own mb-2,
            which an m-0 can't beat. */}
        <div className="mb-8 flex items-center justify-between gap-6 lg:mb-10">
          <H2
            size="lg"
            className="m-0 !mb-0 whitespace-pre-line text-left font-extrabold tracking-tight"
            theme="dark"
          >
            {ukSchoolsTestimonialsTitle}
          </H2>

          {/* Hidden only when every card already fits on screen. */}
          {canPrev || canNext ? (
            <div className="flex shrink-0 gap-3">
              <button
                type="button"
                className={arrowClass}
                onClick={() => scrollByCard(-1)}
                aria-disabled={!canPrev}
                aria-label="Previous quotes"
              >
                <ChevronLeftIcon aria-hidden="true" className="h-5 w-5" />
              </button>
              <button
                type="button"
                className={arrowClass}
                onClick={() => scrollByCard(1)}
                aria-disabled={!canNext}
                aria-label="Next quotes"
              >
                <ChevronRightIcon aria-hidden="true" className="h-5 w-5" />
              </button>
            </div>
          ) : null}
        </div>

        <ScrollAnimate>
          {/* A scrolling element clips everything outside its box, so the
              vertical negative margin and matching padding give the cards'
              drop shadows room below. There is deliberately no horizontal
              bleed: the row is exactly as wide as the content, so the next
              off-screen card does not show as a sliver on the right. */}
          <ul
            ref={trackRef}
            onScroll={updateArrows}
            className={cc([
              "m-0 -my-6 flex list-none items-start gap-6 overflow-x-auto px-0 py-6 lg:gap-8",
              "snap-x snap-mandatory",
              "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            ])}
          >
            {slides.map((slide, index) => (
              <li
                key={slide.key}
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${slides.length}`}
                className={cc([
                  "relative shrink-0 snap-start rounded-3xl bg-zinc-900/60 p-8",
                  // 85% on phones so the next card peeks in. Wider screens
                  // subtract the gaps so exactly two or three cards fit.
                  "w-[85%] sm:w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-4rem)/3)]",
                  // Depth on a dark ground comes from a lighter surface and a
                  // 1px inset highlight along the top edge, the way a raised
                  // surface catches light. A drop shadow alone is invisible here.
                  "shadow-[0_1px_2px_rgba(0,0,0,0.35),0_12px_28px_-12px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.07)]",
                ])}
              >
                {slide.kind === "quote" ? (
                  <QuoteSlide {...slide} />
                ) : (
                  <VodcastSlide
                    title={ukSchoolsVodcastTitle}
                    text={ukSchoolsVodcastText}
                    spotifyUrl={ukSchoolsVodcastSpotifyUrl}
                    youtubeUrl={ukSchoolsVodcastYoutubeUrl}
                  />
                )}
              </li>
            ))}
          </ul>
        </ScrollAnimate>
      </section>
    </div>
  )
}
